import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useSEO } from "../lib/seo";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import { ChevronDown, SlidersHorizontal, X, Check } from "lucide-react";

const SORTS = [["featured", "Featured"], ["price-asc", "Price: low to high"], ["price-desc", "Price: high to low"], ["newest", "Newest first"], ["name", "Name A–Z"]];
const PRICE_BANDS = ["Under ₹500", "₹500–₹999", "₹1000–₹1999", "₹2000+"];

function FilterGroup({ title, name, values, active, onToggle }) {
  const [open, setOpen] = useState(true);
  if (!values?.length) return null;
  return (
    <div className="border-b border-charcoal/10 py-4">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between font-semibold text-sm">
        {title} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {values.map((v) => {
            const isActive = active.includes(v);
            return (
              <li key={v}>
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={isActive} onChange={() => onToggle(name, v)} data-testid={`filter-${name}-${v}`}
                    className="w-4 h-4 accent-[#072835]" />
                  <span className={isActive ? "font-semibold" : ""}>{v}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Collection() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [col, setCol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({});

  const activeFilters = useMemo(() => {
    const f = {};
    for (const [k, v] of params.entries()) if (k.startsWith("f_")) f[k.slice(2)] = v.split("~");
    return f;
  }, [params]);
  const sort = params.get("sort") || "featured";

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/collections`).then((r) => r.json()).then((cols) => {
      setCol(cols.find((c) => c.slug === slug) || { slug: "shop-all", name: "Shop All", intro: "Every joyful thing in the hive, in one place.", color: "#C9DFCC", image: null });
    });
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams(params);
    q.set("collection", slug);
    api.get("/products", { params: Object.fromEntries(q.entries()) })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, params]);

  useSEO({
    title: col ? `${col.name} — Haven Hives` : "Collections — Haven Hives",
    description: col?.intro || "Shop joyful kids' products at Haven Hives.",
  });

  const setFilter = (name, value) => {
    const next = new URLSearchParams(params);
    const cur = (next.get(`f_${name}`) || "").split("~").filter(Boolean);
    const updated = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    if (updated.length) next.set(`f_${name}`, updated.join("~")); else next.delete(`f_${name}`);
    setParams(next, { preventScrollReset: true });
  };
  const setSort = (s) => { const next = new URLSearchParams(params); next.set("sort", s); setParams(next, { preventScrollReset: true }); };
  const clearAll = () => { const next = new URLSearchParams(); next.set("sort", sort); setParams(next, { preventScrollReset: true }); };

  const chips = Object.entries(activeFilters).flatMap(([k, vs]) => vs.map((v) => ({ k, v })));

  const FilterPanel = ({ filters, isDraft }) => (
    <div>
      {filters?.colour && <FilterGroup title="Colour" name="colour" values={filters.colour} active={isDraft ? (draftFilters.colour || []) : (activeFilters.colour || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.capacity && <FilterGroup title="Capacity" name="capacity" values={filters.capacity} active={isDraft ? (draftFilters.capacity || []) : (activeFilters.capacity || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.material && <FilterGroup title="Material" name="material" values={filters.material} active={isDraft ? (draftFilters.material || []) : (activeFilters.material || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.mechanism && <FilterGroup title="Drinking mechanism" name="mechanism" values={filters.mechanism} active={isDraft ? (draftFilters.mechanism || []) : (activeFilters.mechanism || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.compartments && <FilterGroup title="Compartments" name="compartments" values={filters.compartments} active={isDraft ? (draftFilters.compartments || []) : (activeFilters.compartments || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.type && <FilterGroup title="Product type" name="type" values={filters.type} active={isDraft ? (draftFilters.type || []) : (activeFilters.type || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.theme && <FilterGroup title="Theme" name="theme" values={filters.theme} active={isDraft ? (draftFilters.theme || []) : (activeFilters.theme || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.pack_size && <FilterGroup title="Pack size" name="pack_size" values={filters.pack_size} active={isDraft ? (draftFilters.pack_size || []) : (activeFilters.pack_size || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.dimensions && <FilterGroup title="Dimensions" name="dimensions" values={filters.dimensions} active={isDraft ? (draftFilters.dimensions || []) : (activeFilters.dimensions || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.wheels && <FilterGroup title="Wheels" name="wheels" values={filters.wheels} active={isDraft ? (draftFilters.wheels || []) : (activeFilters.wheels || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
      {filters?.price && <FilterGroup title="Price" name="price" values={filters.price} active={isDraft ? (draftFilters.price || []) : (activeFilters.price || [])} onToggle={isDraft ? (n, v) => setDraftFilters((d) => ({ ...d, [n]: (d[n] || []).includes(v) ? d[n].filter((x) => x !== v) : [...(d[n] || []), v] })) : setFilter} />}
    </div>
  );

  return (
    <div data-testid="collection-page">
      {col && (
        <>
          <section className="pt-5 pb-8">
            <div className="mx-auto max-w-[1700px] px-5 sm:px-8 xl:px-14">
              <nav aria-label="Breadcrumb" className="text-sm text-subtext mb-5 flex items-center gap-2">
                <Link to="/" className="hover:text-charcoal">Home</Link> <span aria-hidden="true" className="text-line">|</span> <span className="text-charcoal">{col.name}</span>
              </nav>
              <div className="rounded-2xl overflow-hidden grid lg:grid-cols-2 min-h-[240px] lg:h-[280px]" style={{ background: col.color ? col.color + "66" : "#DAEAEA" }}>
                <div className="px-7 sm:px-14 py-10 lg:py-6 flex flex-col justify-center max-w-xl">
                  <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-tight">{col.name}</h1>
                  {col.intro && <p className="text-[15px] text-subtext mt-4 leading-relaxed">{col.intro}</p>}
                  <p className="text-sm text-subtext mt-4" data-testid="product-count">{loading ? "Loading…" : `${data?.total || 0} products`}</p>
                </div>
                {col.image && <img src={col.image} alt="" aria-hidden="true" width="800" height="400" className="hidden lg:block w-full h-full object-cover" loading="lazy" />}
              </div>
            </div>
          </section>

          <div className="mx-auto max-w-[1700px] px-5 sm:px-8 xl:px-14 pb-20">
            <div className="flex items-center justify-between gap-3 py-4 border-y border-line">
              <button onClick={() => { setDraftFilters(activeFilters); setDrawerOpen(true); }} data-testid="open-filters-btn"
                className="lg:hidden flex items-center gap-2 border border-line bg-white rounded-full px-4 py-2.5 text-sm">
                <SlidersHorizontal className="w-4 h-4" /> Filters {chips.length ? `(${chips.length})` : ""}
              </button>
              <div className="hidden lg:flex items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-2 border border-line rounded-full px-4 py-2.5"><SlidersHorizontal className="w-4 h-4" /> Filter</span>
                <span className="text-subtext">{data?.total || 0} products</span>
                {chips.length > 0 && <button onClick={clearAll} data-testid="clear-all-filters" className="underline underline-offset-4 hover:text-coral">Clear all</button>}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-subtext hidden sm:inline">Sort by:</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="bg-white border border-line rounded-full px-4 py-2.5 text-sm">
                  {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>

            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2 py-4" data-testid="active-filter-chips">
                {chips.map(({ k, v }) => (
                  <span key={k + v} className="flex items-center gap-1.5 bg-surface rounded-full pl-3 pr-2 py-1.5 text-xs">
                    {v}
                    <button onClick={() => setFilter(k, v)} aria-label={`Remove filter ${v}`} data-testid={`remove-chip-${v}`}><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
                <button onClick={clearAll} className="underline underline-offset-4 text-xs hover:text-coral">Clear all</button>
              </div>
            )}

            <div className="flex gap-10 mt-6">
              <aside className="hidden lg:block w-60 shrink-0" aria-label="Product filters">
                <h2 className="font-display text-2xl mb-1">Filters</h2>
                <FilterPanel filters={data?.facets} />
              </aside>
              <div className="flex-1">
                {loading ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" aria-busy="true">
                    {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-surface animate-pulse" />)}
                  </div>
                ) : data?.products?.length ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-grid">
                    {data.products.map((p, i) => <ProductCard key={p.slug} p={p} priority={i < 4} />)}
                  </div>
                ) : (
                  <div className="text-center py-20 bg-surface rounded-2xl" data-testid="empty-state">
                    <p className="font-display text-2xl">Nothing matches those filters just yet</p>
                    <p className="text-sm text-subtext mt-2">Try removing a filter or two — the hive is always growing.</p>
                    <button onClick={clearAll} className="btn-primary mt-6">Clear all filters</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true" aria-label="Filters">
          <div className="absolute inset-0 bg-forest/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl p-5 safe-bottom" data-testid="filter-drawer">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-2xl">Filters</h2>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close filters" data-testid="filter-drawer-close" className="p-2 rounded-full hover:bg-surface"><X className="w-5 h-5" /></button>
            </div>
            {data?.facets && Object.keys(data.facets).length ? <FilterPanel filters={data.facets} isDraft /> : <p className="text-sm text-subtext py-6" data-testid="filters-empty">No extra filters for this collection — use sort, or browse a category for colour, size and price filters.</p>}
            <div className="flex gap-3 mt-6 sticky bottom-0 bg-white pt-3">
              <button onClick={() => { setDraftFilters({}); clearAll(); setDrawerOpen(false); }} className="btn-secondary flex-1" data-testid="filter-clear">Clear</button>
              <button onClick={() => { const next = new URLSearchParams(); next.set("sort", sort); Object.entries(draftFilters).forEach(([k, vs]) => vs.length && next.set(`f_${k}`, vs.join("~"))); setParams(next, { preventScrollReset: true }); setDrawerOpen(false); }} className="btn-primary flex-1" data-testid="filter-apply">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
